import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  CreditCard,
  Truck,
  Shield,
  Tag,
  ChevronRight,
  Check,
  MapPin,
  Gift,
  Coins,
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { RadioGroup, RadioGroupItem } from '../../components/ui/radio-group';
import { Slider } from '../../components/ui/slider';
import { useCart } from '../../context/CartContext';
import { useAuth } from '../../context/AuthContext';
import { mockUser } from '../../data/mock';

const CheckoutPage = () => {
  const navigate = useNavigate();
  const { items, subtotal, shipping, discountAmount, total, clearCart } = useCart();
  const { isAuthenticated, user } = useAuth();

  const [step, setStep] = useState(1);
  const [isProcessing, setIsProcessing] = useState(false);

  // Form State
  const [shippingInfo, setShippingInfo] = useState({
    firstName: user?.name?.split(' ')[0] || '',
    lastName: user?.name?.split(' ')[1] || '',
    email: user?.email || '',
    phone: '',
    address: mockUser.savedAddresses[0]?.address || '',
    city: mockUser.savedAddresses[0]?.city || '',
    state: mockUser.savedAddresses[0]?.state || '',
    zip: mockUser.savedAddresses[0]?.zip || '',
  });

  const [shippingMethod, setShippingMethod] = useState('standard');
  const [paymentMethod, setPaymentMethod] = useState('card');
  const [loyaltyPoints, setLoyaltyPoints] = useState(0);

  const maxPoints = mockUser.loyaltyPoints;
  const pointsValue = loyaltyPoints * 0.01; // 1 point = $0.01

  const shippingOptions = [
    { id: 'standard', name: 'Standard Shipping', time: '5-7 business days', price: shipping },
    { id: 'express', name: 'Express Shipping', time: '2-3 business days', price: 12.99 },
    { id: 'overnight', name: 'Overnight Shipping', time: '1 business day', price: 24.99 },
  ];

  const selectedShipping = shippingOptions.find((s) => s.id === shippingMethod);
  const finalTotal = total - pointsValue + (selectedShipping?.price || 0) - shipping;

  const handlePlaceOrder = async () => {
    setIsProcessing(true);
    // Simulate order processing
    await new Promise((resolve) => setTimeout(resolve, 2000));
    clearCart();
    navigate('/order-confirmation', {
      state: {
        orderId: `ORD-${Date.now()}`,
        total: finalTotal,
        items: items,
        shippingMethod: selectedShipping,
      },
    });
  };

  if (items.length === 0) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Your cart is empty</h2>
          <p className="text-gray-500 mb-6">Add items to your cart to checkout</p>
          <Link to="/shop">
            <Button className="bg-red-600 hover:bg-red-700 text-white rounded-full px-8">
              Browse Products
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-8">
          <Link to="/cart" className="flex items-center gap-2 text-gray-600 hover:text-red-600 mb-4">
            <ArrowLeft className="w-4 h-4" />
            <span className="text-sm">Back to Cart</span>
          </Link>
          <h1 className="text-2xl md:text-3xl font-bold text-gray-900">Checkout</h1>
        </div>

        {/* Progress Steps */}
        <div className="flex items-center justify-center mb-8">
          {['Shipping', 'Payment', 'Review'].map((label, index) => (
            <React.Fragment key={label}>
              <div className="flex items-center">
                <div
                  className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold transition-all ${
                    step > index + 1
                      ? 'bg-emerald-500 text-white'
                      : step === index + 1
                      ? 'bg-red-600 text-white'
                      : 'bg-gray-200 text-gray-500'
                  }`}
                >
                  {step > index + 1 ? <Check className="w-4 h-4" /> : index + 1}
                </div>
                <span
                  className={`ml-2 text-sm font-medium ${
                    step === index + 1 ? 'text-gray-900' : 'text-gray-500'
                  }`}
                >
                  {label}
                </span>
              </div>
              {index < 2 && (
                <div
                  className={`w-16 md:w-24 h-0.5 mx-2 ${
                    step > index + 1 ? 'bg-emerald-500' : 'bg-gray-200'
                  }`}
                />
              )}
            </React.Fragment>
          ))}
        </div>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2">
            {/* Step 1: Shipping */}
            {step === 1 && (
              <Card className="border-gray-100">
                <CardContent className="p-6">
                  <h2 className="text-lg font-bold text-gray-900 mb-6 flex items-center gap-2">
                    <MapPin className="w-5 h-5 text-red-600" />
                    Shipping Information
                  </h2>

                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="firstName">First Name</Label>
                      <Input
                        id="firstName"
                        value={shippingInfo.firstName}
                        onChange={(e) => setShippingInfo({ ...shippingInfo, firstName: e.target.value })}
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label htmlFor="lastName">Last Name</Label>
                      <Input
                        id="lastName"
                        value={shippingInfo.lastName}
                        onChange={(e) => setShippingInfo({ ...shippingInfo, lastName: e.target.value })}
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label htmlFor="email">Email</Label>
                      <Input
                        id="email"
                        type="email"
                        value={shippingInfo.email}
                        onChange={(e) => setShippingInfo({ ...shippingInfo, email: e.target.value })}
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label htmlFor="phone">Phone</Label>
                      <Input
                        id="phone"
                        type="tel"
                        value={shippingInfo.phone}
                        onChange={(e) => setShippingInfo({ ...shippingInfo, phone: e.target.value })}
                        className="mt-1"
                      />
                    </div>
                    <div className="md:col-span-2">
                      <Label htmlFor="address">Address</Label>
                      <Input
                        id="address"
                        value={shippingInfo.address}
                        onChange={(e) => setShippingInfo({ ...shippingInfo, address: e.target.value })}
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label htmlFor="city">City</Label>
                      <Input
                        id="city"
                        value={shippingInfo.city}
                        onChange={(e) => setShippingInfo({ ...shippingInfo, city: e.target.value })}
                        className="mt-1"
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="state">State</Label>
                        <Input
                          id="state"
                          value={shippingInfo.state}
                          onChange={(e) => setShippingInfo({ ...shippingInfo, state: e.target.value })}
                          className="mt-1"
                        />
                      </div>
                      <div>
                        <Label htmlFor="zip">ZIP Code</Label>
                        <Input
                          id="zip"
                          value={shippingInfo.zip}
                          onChange={(e) => setShippingInfo({ ...shippingInfo, zip: e.target.value })}
                          className="mt-1"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Shipping Method */}
                  <div className="mt-8">
                    <h3 className="text-md font-semibold text-gray-900 mb-4 flex items-center gap-2">
                      <Truck className="w-5 h-5 text-red-600" />
                      Shipping Method
                    </h3>
                    <RadioGroup value={shippingMethod} onValueChange={setShippingMethod}>
                      <div className="space-y-3">
                        {shippingOptions.map((option) => (
                          <label
                            key={option.id}
                            className={`flex items-center justify-between p-4 border rounded-xl cursor-pointer transition-all ${
                              shippingMethod === option.id
                                ? 'border-red-500 bg-red-50'
                                : 'border-gray-200 hover:border-gray-300'
                            }`}
                          >
                            <div className="flex items-center gap-3">
                              <RadioGroupItem value={option.id} id={option.id} />
                              <div>
                                <p className="font-medium text-gray-900">{option.name}</p>
                                <p className="text-sm text-gray-500">{option.time}</p>
                              </div>
                            </div>
                            <span className="font-semibold text-gray-900">
                              {option.price === 0 ? 'FREE' : `$${option.price.toFixed(2)}`}
                            </span>
                          </label>
                        ))}
                      </div>
                    </RadioGroup>
                  </div>

                  <Button
                    onClick={() => setStep(2)}
                    className="w-full mt-6 bg-red-600 hover:bg-red-700 text-white rounded-full h-12"
                  >
                    Continue to Payment
                    <ChevronRight className="ml-2 w-4 h-4" />
                  </Button>
                </CardContent>
              </Card>
            )}

            {/* Step 2: Payment */}
            {step === 2 && (
              <Card className="border-gray-100">
                <CardContent className="p-6">
                  <h2 className="text-lg font-bold text-gray-900 mb-6 flex items-center gap-2">
                    <CreditCard className="w-5 h-5 text-red-600" />
                    Payment Method
                  </h2>

                  <RadioGroup value={paymentMethod} onValueChange={setPaymentMethod}>
                    <div className="space-y-3">
                      {[
                        { id: 'card', name: 'Credit / Debit Card', icon: CreditCard },
                        { id: 'paypal', name: 'PayPal', icon: Shield },
                        { id: 'apple', name: 'Apple Pay', icon: Shield },
                      ].map((option) => (
                        <label
                          key={option.id}
                          className={`flex items-center gap-3 p-4 border rounded-xl cursor-pointer transition-all ${
                            paymentMethod === option.id
                              ? 'border-red-500 bg-red-50'
                              : 'border-gray-200 hover:border-gray-300'
                          }`}
                        >
                          <RadioGroupItem value={option.id} id={option.id} />
                          <option.icon className="w-5 h-5 text-gray-400" />
                          <span className="font-medium text-gray-900">{option.name}</span>
                        </label>
                      ))}
                    </div>
                  </RadioGroup>

                  {paymentMethod === 'card' && (
                    <div className="mt-6 space-y-4">
                      <div>
                        <Label htmlFor="cardNumber">Card Number</Label>
                        <Input
                          id="cardNumber"
                          placeholder="1234 5678 9012 3456"
                          className="mt-1"
                        />
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label htmlFor="expiry">Expiry Date</Label>
                          <Input id="expiry" placeholder="MM/YY" className="mt-1" />
                        </div>
                        <div>
                          <Label htmlFor="cvv">CVV</Label>
                          <Input id="cvv" placeholder="123" className="mt-1" />
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Loyalty Points */}
                  <div className="mt-8 p-4 bg-gradient-to-r from-amber-50 to-yellow-50 border border-amber-200 rounded-xl">
                    <div className="flex items-center gap-2 mb-3">
                      <Coins className="w-5 h-5 text-amber-600" />
                      <span className="font-semibold text-amber-800">Use Loyalty Points</span>
                    </div>
                    <p className="text-sm text-amber-700 mb-3">
                      You have <span className="font-bold">{maxPoints}</span> points (${(maxPoints * 0.01).toFixed(2)} value)
                    </p>
                    <Slider
                      value={[loyaltyPoints]}
                      onValueChange={([value]) => setLoyaltyPoints(value)}
                      max={Math.min(maxPoints, total * 100)}
                      step={100}
                      className="mb-2"
                    />
                    <div className="flex justify-between text-sm">
                      <span className="text-amber-600">Using: {loyaltyPoints} points</span>
                      <span className="font-medium text-amber-800">-${pointsValue.toFixed(2)}</span>
                    </div>
                  </div>

                  <div className="flex gap-3 mt-6">
                    <Button variant="outline" onClick={() => setStep(1)} className="flex-1 h-12 rounded-full">
                      <ArrowLeft className="mr-2 w-4 h-4" />
                      Back
                    </Button>
                    <Button
                      onClick={() => setStep(3)}
                      className="flex-1 bg-red-600 hover:bg-red-700 text-white rounded-full h-12"
                    >
                      Review Order
                      <ChevronRight className="ml-2 w-4 h-4" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Step 3: Review */}
            {step === 3 && (
              <Card className="border-gray-100">
                <CardContent className="p-6">
                  <h2 className="text-lg font-bold text-gray-900 mb-6">Review Your Order</h2>

                  {/* Shipping Summary */}
                  <div className="p-4 bg-gray-50 rounded-xl mb-4">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium text-gray-500">Shipping to</span>
                      <button onClick={() => setStep(1)} className="text-sm text-red-600 hover:underline">
                        Edit
                      </button>
                    </div>
                    <p className="text-gray-900">
                      {shippingInfo.firstName} {shippingInfo.lastName}
                    </p>
                    <p className="text-gray-600 text-sm">
                      {shippingInfo.address}, {shippingInfo.city}, {shippingInfo.state} {shippingInfo.zip}
                    </p>
                    <p className="text-gray-600 text-sm mt-1">{selectedShipping?.name} ({selectedShipping?.time})</p>
                  </div>

                  {/* Payment Summary */}
                  <div className="p-4 bg-gray-50 rounded-xl mb-6">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium text-gray-500">Payment</span>
                      <button onClick={() => setStep(2)} className="text-sm text-red-600 hover:underline">
                        Edit
                      </button>
                    </div>
                    <p className="text-gray-900 capitalize">{paymentMethod}</p>
                    {loyaltyPoints > 0 && (
                      <p className="text-amber-600 text-sm mt-1">
                        Using {loyaltyPoints} loyalty points (-${pointsValue.toFixed(2)})
                      </p>
                    )}
                  </div>

                  {/* Items */}
                  <div className="space-y-3 mb-6">
                    {items.map((item) => (
                      <div key={item.productId} className="flex items-center gap-3">
                        <div className="w-16 h-16 bg-gray-100 rounded-lg overflow-hidden">
                          <img
                            src={item.product?.images?.[0] || 'https://placehold.co/64x64'}
                            alt={item.product?.name}
                            className="w-full h-full object-cover"
                          />
                        </div>
                        <div className="flex-1">
                          <p className="font-medium text-gray-900 text-sm">{item.product?.name}</p>
                          <p className="text-gray-500 text-sm">Qty: {item.quantity}</p>
                        </div>
                        <p className="font-semibold text-gray-900">
                          ${(item.product?.price * item.quantity).toFixed(2)}
                        </p>
                      </div>
                    ))}
                  </div>

                  <div className="flex gap-3">
                    <Button variant="outline" onClick={() => setStep(2)} className="flex-1 h-12 rounded-full">
                      <ArrowLeft className="mr-2 w-4 h-4" />
                      Back
                    </Button>
                    <Button
                      onClick={handlePlaceOrder}
                      disabled={isProcessing}
                      className="flex-1 bg-red-600 hover:bg-red-700 text-white rounded-full h-12"
                    >
                      {isProcessing ? 'Processing...' : 'Place Order'}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>

          {/* Order Summary Sidebar */}
          <div className="lg:col-span-1">
            <Card className="border-gray-100 sticky top-24">
              <CardContent className="p-6">
                <h2 className="text-lg font-bold text-gray-900 mb-4">Order Summary</h2>

                {/* Items Preview */}
                <div className="space-y-3 mb-6 max-h-48 overflow-y-auto">
                  {items.map((item) => (
                    <div key={item.productId} className="flex items-center gap-3">
                      <div className="w-12 h-12 bg-gray-100 rounded-lg overflow-hidden flex-shrink-0">
                        <img
                          src={item.product?.images?.[0] || 'https://placehold.co/48x48'}
                          alt={item.product?.name}
                          className="w-full h-full object-cover"
                        />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900 truncate">{item.product?.name}</p>
                        <p className="text-xs text-gray-500">x{item.quantity}</p>
                      </div>
                      <p className="text-sm font-semibold text-gray-900">
                        ${(item.product?.price * item.quantity).toFixed(2)}
                      </p>
                    </div>
                  ))}
                </div>

                {/* Totals */}
                <div className="space-y-2 pt-4 border-t border-gray-100">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Subtotal</span>
                    <span className="text-gray-900">${subtotal.toFixed(2)}</span>
                  </div>
                  {discountAmount > 0 && (
                    <div className="flex justify-between text-sm">
                      <span className="text-emerald-600">Discount</span>
                      <span className="text-emerald-600">-${discountAmount.toFixed(2)}</span>
                    </div>
                  )}
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Shipping</span>
                    <span className="text-gray-900">
                      {selectedShipping?.price === 0 ? 'FREE' : `$${(selectedShipping?.price || 0).toFixed(2)}`}
                    </span>
                  </div>
                  {pointsValue > 0 && (
                    <div className="flex justify-between text-sm">
                      <span className="text-amber-600">Loyalty Points</span>
                      <span className="text-amber-600">-${pointsValue.toFixed(2)}</span>
                    </div>
                  )}
                </div>

                <div className="flex justify-between items-center pt-4 mt-4 border-t border-gray-100">
                  <span className="text-lg font-bold text-gray-900">Total</span>
                  <span className="text-2xl font-bold text-gray-900">${finalTotal.toFixed(2)}</span>
                </div>

                {/* Trust */}
                <div className="mt-6 pt-4 border-t border-gray-100 flex items-center justify-center gap-4 text-xs text-gray-500">
                  <Shield className="w-4 h-4" />
                  <span>Secure 256-bit SSL encryption</span>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      {/* Footer Spacer for mobile nav */}
      <div className="h-20 md:h-0" />
    </div>
  );
};

export default CheckoutPage;